// Story 1.21 — main application form. Two-column on desktop (form +
// sticky cert preview); stacked on mobile. Live preview updates as the
// user types so the snapshot rule is felt before submit.

import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Textarea,
} from "@greenhold/components";

import { CreatorApplicationCertPreview } from "./CreatorApplicationCertPreview";
import { ImageDropzone } from "./ImageDropzone";
import { SignatureDropzone } from "./SignatureDropzone";
import { LinkedAccountsField } from "./LinkedAccountsField";
import { IdentityVerifyField } from "./IdentityVerifyField";
import {
  type LinkedAccountInput,
  useSubmitCreatorApplication,
} from "./useSubmitCreatorApplication";
import {
  useCreatorApplicationDraft,
  useSaveCreatorApplicationDraft,
} from "./useCreatorApplicationDraft";

const REGIONS = [
  { value: "GB", label: "United Kingdom" },
  { value: "GB-ENG", label: "England" },
  { value: "GB-WLS", label: "Wales" },
  { value: "GB-SCT", label: "Scotland" },
  { value: "GB-NIR", label: "Northern Ireland" },
  { value: "US", label: "United States" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "PT", label: "Portugal" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "PL", label: "Poland" },
  { value: "IE", label: "Ireland" },
  { value: "AU", label: "Australia" },
  { value: "CA", label: "Canada" },
];

const LANGUAGES = [
  { value: "en-GB", label: "English (UK)" },
  { value: "en-US", label: "English (US)" },
  { value: "de-DE", label: "German" },
  { value: "fr-FR", label: "French" },
  { value: "es-ES", label: "Spanish" },
  { value: "pt-PT", label: "Portuguese" },
  { value: "it-IT", label: "Italian" },
  { value: "nl-NL", label: "Dutch" },
  { value: "sv-SE", label: "Swedish" },
  { value: "no-NO", label: "Norwegian" },
  { value: "da-DK", label: "Danish" },
];

// 38, not the server's 40 hard cap: longest name that still renders on
// ONE line on the PatronageCertificate artifact. Keep in sync with
// CreatorProfileForm.
const DISPLAY_NAME_MAX = 38;
const HELLO_TEXT_MAX = 140;
const COUNTER_AMBER_THRESHOLD = 0.9;

// Story 8.9 — identity caps, equal to the server validators and to
// migration 0094's CHECKs, so a maxLength never disagrees with a 400.
const LEGAL_NAME_MAX = 120;
const ADDRESS_LINE_MAX = 120;
const POSTAL_CODE_MAX = 20;
const CITY_MAX = 80;
const BUSINESS_REG_NUMBER_MAX = 32;

/** 2-letter ISO country code, uppercased, letters only.
 *
 *  A 249-entry picker list is NOT duplicated into the SPA: the repo keeps
 *  exactly one list of assigned ISO 3166-1 alpha-2 codes
 *  (`server/src/domain/country_register.rs`), and the server is the authority
 *  on whether a code is real — same division of labour as the country fields
 *  on WalletDetailsPanel. This only fixes the SHAPE. */
function normalizeCountry(raw: string): string {
  return raw.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase();
}

type BusinessStatus = "" | "private" | "business";

type ActiveTos = {
  id: string;
  version_label: string;
  document_kind: string;
};

/** STORY 1.33 — `?kind=creator_program` IS THE FIX THIS STORY EXISTS FOR.
 *
 *  This form's accept-checkbox is the applicant's consent to the CREATOR
 *  PROGRAM terms, and the id it submits is what gets stored as the proof of
 *  that consent. Before this story the endpoint served the single globally-
 *  active document — in practice the v1.0 user-terms placeholder — so every
 *  application on record attested to a document about buying dossiers.
 *
 *  The backend now rejects a non-creator-program id outright
 *  (`creator_application_service::submit_application`), so a regression here
 *  is a 400 rather than another bad consent record. */
async function fetchActiveTos(): Promise<ActiveTos> {
  const resp = await fetch("/api/v1/legal/current-tos?kind=creator_program");
  if (!resp.ok) {
    throw new Error(`current-tos fetch failed: ${resp.status}`);
  }
  const body = (await resp.json()) as ActiveTos;
  return body;
}

interface Props {
  showRejectedHint?: boolean;
}

export function CreatorApplicationForm({ showRejectedHint = false }: Props) {
  const navigate = useNavigate();
  const submitMutation = useSubmitCreatorApplication();
  const draftQuery = useCreatorApplicationDraft();
  const saveDraftMutation = useSaveCreatorApplicationDraft();

  const [displayName, setDisplayName] = useState("");
  const [helloText, setHelloText] = useState("");
  const [imageBlobPath, setImageBlobPath] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [signatureBlobPath, setSignatureBlobPath] = useState<string | null>(
    null,
  );
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(
    null,
  );
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccountInput[]>([
    { kind: "twitch", url: "" },
  ]);
  const [region, setRegion] = useState("GB");
  const [language, setLanguage] = useState("en-GB");
  // Story 8.9 — identity. `businessStatus` starts EMPTY on purpose: it is a
  // declaration about the capacity the applicant acts in, and a pre-ticked
  // default would make it something they never actually stated.
  const [legalName, setLegalName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [businessStatus, setBusinessStatus] = useState<BusinessStatus>("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [activeTos, setActiveTos] = useState<ActiveTos | null>(null);
  const [linkErrors, setLinkErrors] = useState<Record<number, string>>({});
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  useEffect(() => {
    fetchActiveTos()
      .then(setActiveTos)
      .catch(() => {
        toast.error("Couldn't load creator-program terms. Refresh to retry.");
      });
  }, []);

  // Rehydrate a saved draft ONCE on first load so returning to the page
  // (or another device) restores work in progress.
  //
  // ⚠ THE GUARDS ARE `rehydratedRef` PLUS A TRUTHY CHECK PER FIELD, AND THAT IS
  // NOT THE SAME AS "never clobbers edits". They stop a SECOND rehydration and
  // stop a null draft field from blanking a populated input — but if the user
  // starts typing BEFORE the draft GET resolves, the arriving draft overwrites
  // what they typed. Pre-existing behaviour, unchanged here: the Story 8.9
  // identity fields are guarded exactly as `display_name` and `hello_text`
  // already were, so the race is one shape for the whole form rather than two.
  const rehydratedRef = useRef(false);
  useEffect(() => {
    if (rehydratedRef.current) return;
    const draft = draftQuery.data?.data;
    if (!draft) return;
    rehydratedRef.current = true;
    if (draft.display_name) setDisplayName(draft.display_name);
    if (draft.hello_text) setHelloText(draft.hello_text);
    if (draft.image_blob_path) setImageBlobPath(draft.image_blob_path);
    if (draft.image_signed_url) setImagePreviewUrl(draft.image_signed_url);
    if (draft.signature_blob_path)
      setSignatureBlobPath(draft.signature_blob_path);
    if (draft.signature_signed_url)
      setSignaturePreviewUrl(draft.signature_signed_url);
    if (draft.linked_accounts && draft.linked_accounts.length > 0)
      setLinkedAccounts(draft.linked_accounts);
    if (draft.region) setRegion(draft.region);
    if (draft.language) setLanguage(draft.language);
    // Story 8.9 — restore the identity block. Guarded like the fields above so
    // a null never clobbers something already typed.
    if (draft.legal_name) setLegalName(draft.legal_name);
    if (draft.address_line1) setAddressLine1(draft.address_line1);
    if (draft.address_line2) setAddressLine2(draft.address_line2);
    if (draft.postal_code) setPostalCode(draft.postal_code);
    if (draft.city) setCity(draft.city);
    if (draft.country_code) setCountryCode(draft.country_code);
    if (draft.business_status === "private" || draft.business_status === "business") {
      setBusinessStatus(draft.business_status);
    }
    if (draft.business_registration_number) {
      setRegistrationNumber(draft.business_registration_number);
    }
    setDraftSavedAt(draft.updated_at);
  }, [draftQuery.data]);

  // Clean up freshly-uploaded Object URLs on unmount (rehydrated previews
  // are signed https URLs, not blob: URLs, so they're left alone).
  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);
  useEffect(() => {
    return () => {
      if (signaturePreviewUrl && signaturePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(signaturePreviewUrl);
      }
    };
  }, [signaturePreviewUrl]);

  // Submit stays clickable at all times — clicking it with an
  // incomplete form marks every offending field instead of silently
  // doing nothing (the old disabled-button UX gave no clue what was
  // missing). Keys: display_name, hello_text, image, signature, tos.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function clearFieldError(key: string) {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  // Persists the current form as a draft; THROWS on failure so callers
  // that gate a side effect on a successful save (e.g. the Twitch OAuth
  // redirect) can bail instead of navigating away with unsaved work.
  const persistDraft = async () => {
    const saved = await saveDraftMutation.mutateAsync({
      display_name: displayName.trim() || null,
      hello_text: helloText.trim() || null,
      image_blob_path: imageBlobPath,
      signature_blob_path: signatureBlobPath,
      linked_accounts: linkedAccounts.filter(
        (la) => la.url.trim().length > 0 || la.kind.trim().length > 0,
      ),
      region,
      language,
      tos_version_accepted_id: activeTos?.id ?? null,
      // Story 8.9 — the identity block goes into the draft too. Without it,
      // "Connect Twitch" (which calls this and then navigates away) would
      // throw away everything typed here.
      legal_name: legalName.trim() || null,
      address_line1: addressLine1.trim() || null,
      address_line2: addressLine2.trim() || null,
      postal_code: postalCode.trim() || null,
      city: city.trim() || null,
      country_code: normalizeCountry(countryCode) || null,
      business_status: businessStatus || null,
      business_registration_number:
        businessStatus === "business"
          ? registrationNumber.trim() || null
          : null,
    });
    setDraftSavedAt(saved.updated_at);
  };

  const handleSaveDraft = async () => {
    try {
      await persistDraft();
    } catch (err) {
      const detail = err as { message?: string };
      toast.error(detail.message ?? "Couldn't save draft. Try again.");
    }
  };

  const draftSavedLabel = draftSavedAt
    ? new Date(draftSavedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation pass: collect EVERY problem, mark the
    // fields, and stop. The button itself never disables on
    // incompleteness — this is where "why can't I submit" gets answered.
    const errs: Record<string, string> = {};
    if (!displayName.trim()) errs.display_name = "Display name is required.";
    if (!helloText.trim()) errs.hello_text = "Hello text is required.";
    if (!imageBlobPath) errs.image = "Upload a square photo or logo.";
    if (!signatureBlobPath) errs.signature = "Upload your signature.";
    if (!legalName.trim()) errs.legal_name = "Legal name is required.";
    if (!addressLine1.trim()) errs.address_line1 = "Address is required.";
    if (!postalCode.trim()) errs.postal_code = "Postal code is required.";
    if (!city.trim()) errs.city = "City is required.";
    if (normalizeCountry(countryCode).length !== 2) {
      errs.country_code = "Use a 2-letter country code, e.g. DK.";
    }
    if (!businessStatus) {
      errs.business_status = "Choose whether you are applying as a private individual or a business.";
    }
    if (!tosAccepted) {
      errs.tos = "Accept the creator-program terms to submit.";
    } else if (!activeTos) {
      errs.tos = "Terms couldn't be loaded — refresh the page and retry.";
    }
    const linkErrs: Record<number, string> = {};
    const filledLinks = linkedAccounts.filter((la) => la.url.trim().length > 0);
    if (filledLinks.length === 0) {
      linkErrs[0] = "At least one link is required.";
    }
    linkedAccounts.forEach((la, i) => {
      const url = la.url.trim();
      if (url && !url.startsWith("https://")) {
        linkErrs[i] = "Must be a full https:// URL.";
      }
    });
    setFieldErrors(errs);
    setLinkErrors(linkErrs);
    if (Object.keys(errs).length > 0 || Object.keys(linkErrs).length > 0) {
      toast.error("Fix the highlighted fields before submitting.");
      return;
    }

    try {
      await submitMutation.mutateAsync({
        display_name: displayName.trim(),
        hello_text: helloText.trim(),
        image_blob_path: imageBlobPath as string,
        signature_blob_path: signatureBlobPath as string,
        // Empty rows are UI scaffolding, not data — submit only filled ones.
        linked_accounts: filledLinks,
        region,
        language,
        tos_version_accepted_id: (activeTos as ActiveTos).id,
        legal_name: legalName.trim(),
        address_line1: addressLine1.trim(),
        // Omitted rather than sent blank when the address is one line.
        ...(addressLine2.trim()
          ? { address_line2: addressLine2.trim() }
          : {}),
        postal_code: postalCode.trim(),
        city: city.trim(),
        country_code: normalizeCountry(countryCode),
        business_status: businessStatus as "private" | "business",
        // Only a business can carry one, and only when they gave one —
        // the server REFUSES the private + number pair rather than
        // quietly dropping half of a contradictory declaration.
        ...(businessStatus === "business" && registrationNumber.trim()
          ? { business_registration_number: registrationNumber.trim() }
          : {}),
      });
      toast.success("Application submitted — we'll review within 48 hours");
      // Force a re-fetch of the status query so the page re-renders
      // the pending card.
      navigate("/account/become-creator", { replace: true });
    } catch (err) {
      const detail = err as {
        code?: string;
        message?: string;
        reason?: string;
        /** Story 8.9 — the field a VALIDATION_FAILED names (AppError::Validation). */
        field?: string;
        failures?: Array<{ index: number; reason: string }>;
      };
      if (detail.code === "LINKED_ACCOUNT_VALIDATION_FAILED" && detail.failures) {
        const errs: Record<number, string> = {};
        for (const f of detail.failures) {
          errs[f.index] = `Could not verify (${f.reason})`;
        }
        setLinkErrors(errs);
        toast.error("Some linked accounts couldn't be verified — fix the highlighted rows.");
      } else if (detail.code === "CREATOR_APPLICATION_CONFLICT") {
        toast.error(detail.message ?? "Application already exists.");
        navigate("/account/become-creator", { replace: true });
      } else if (detail.code === "VALIDATION_FAILED" && detail.field) {
        // The server is the authority on the identity block (an unassigned
        // country code passes the client's shape check and fails there), so
        // mark the field it names instead of only toasting.
        setFieldErrors({
          [detail.field]: detail.message ?? "Check this field.",
        });
        toast.error("Fix the highlighted fields before submitting.");
      } else {
        toast.error(detail.message ?? "Submit failed. Try again.");
      }
    }
  };

  const displayCounterColor =
    displayName.length / DISPLAY_NAME_MAX >= COUNTER_AMBER_THRESHOLD
      ? "text-amber-700"
      : "opacity-60";
  const helloCounterColor =
    helloText.length / HELLO_TEXT_MAX >= COUNTER_AMBER_THRESHOLD
      ? "text-amber-700"
      : "opacity-60";

  return (
    <form
      onSubmit={handleSubmit}
      // Our submit handler runs the validation + per-field marking;
      // native browser validation would block onSubmit before it fires.
      noValidate
      className="grid grid-cols-1 lg:grid-cols-2 gap-8"
    >
      <div className="space-y-6">
        {showRejectedHint && (
          <Card className="bg-brand-paper-50 border-brand-forest-200">
            <CardContent className="p-4">
              <p className="text-sm">
                <strong>Welcome back.</strong> Your previous application was
                rejected; you can re-apply now. We review each application on
                its own merits.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-1">
          <Label htmlFor="display_name">Display name *</Label>
          <Input
            id="display_name"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              clearFieldError("display_name");
            }}
            maxLength={DISPLAY_NAME_MAX}
            aria-invalid={!!fieldErrors.display_name}
          />
          {fieldErrors.display_name ? (
            <p className="text-sm text-destructive">{fieldErrors.display_name}</p>
          ) : null}
          <p className={`text-xs text-right ${displayCounterColor}`}>
            {displayName.length}/{DISPLAY_NAME_MAX} characters
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="hello_text">Hello text *</Label>
          <Textarea
            id="hello_text"
            value={helloText}
            onChange={(e) => {
              setHelloText(e.target.value);
              clearFieldError("hello_text");
            }}
            maxLength={HELLO_TEXT_MAX}
            rows={3}
            aria-invalid={!!fieldErrors.hello_text}
          />
          {fieldErrors.hello_text ? (
            <p className="text-sm text-destructive">{fieldErrors.hello_text}</p>
          ) : null}
          <p className={`text-xs text-right ${helloCounterColor}`}>
            {helloText.length}/{HELLO_TEXT_MAX} characters
          </p>
        </div>

        <div className="space-y-1">
          <ImageDropzone
            blobPath={imageBlobPath}
            previewUrl={imagePreviewUrl}
            onUploaded={(blobPath, preview) => {
              setImageBlobPath(blobPath);
              setImagePreviewUrl(preview);
              clearFieldError("image");
            }}
            onRemoved={() => {
              setImageBlobPath(null);
              setImagePreviewUrl(null);
            }}
          />
          {fieldErrors.image ? (
            <p className="text-sm text-destructive">{fieldErrors.image}</p>
          ) : null}
        </div>

        <div className="space-y-1">
          <SignatureDropzone
            blobPath={signatureBlobPath}
            previewUrl={signaturePreviewUrl}
            onUploaded={(blobPath, preview) => {
              setSignatureBlobPath(blobPath);
              setSignaturePreviewUrl(preview);
              clearFieldError("signature");
            }}
            onRemoved={() => {
              setSignatureBlobPath(null);
              setSignaturePreviewUrl(null);
            }}
          />
          {fieldErrors.signature ? (
            <p className="text-sm text-destructive">{fieldErrors.signature}</p>
          ) : null}
        </div>

        <LinkedAccountsField
          rows={linkedAccounts}
          errors={linkErrors}
          onChange={(next) => {
            setLinkedAccounts(next);
            setLinkErrors({});
          }}
        />

        <IdentityVerifyField onBeforeConnect={persistDraft} />

        {/* Story 8.9 — who we contract with and pay. Plain labels, no
            explanation of what any of it is used for beyond the one line
            below: this is a sign-up form, not a notice. */}
        <fieldset className="space-y-4 rounded-md border border-brand-forest-200 bg-brand-paper-50 p-4">
          <legend className="px-1 text-sm font-semibold">Your details</legend>
          <p className="text-xs opacity-70 -mt-2">
            We need these to pay you.
          </p>

          <div className="space-y-1">
            <Label htmlFor="legal_name">Legal name *</Label>
            <Input
              id="legal_name"
              value={legalName}
              autoComplete="name"
              maxLength={LEGAL_NAME_MAX}
              onChange={(e) => {
                setLegalName(e.target.value);
                clearFieldError("legal_name");
              }}
              aria-invalid={!!fieldErrors.legal_name}
            />
            {fieldErrors.legal_name ? (
              <p className="text-sm text-destructive">{fieldErrors.legal_name}</p>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label htmlFor="address_line1">Address *</Label>
            <Input
              id="address_line1"
              value={addressLine1}
              autoComplete="address-line1"
              maxLength={ADDRESS_LINE_MAX}
              onChange={(e) => {
                setAddressLine1(e.target.value);
                clearFieldError("address_line1");
              }}
              aria-invalid={!!fieldErrors.address_line1}
            />
            {/* Second line of the same field, not a second field: plenty of
                addresses are one line, and the server treats it as optional. */}
            <Input
              id="address_line2"
              value={addressLine2}
              autoComplete="address-line2"
              maxLength={ADDRESS_LINE_MAX}
              aria-label="Address line 2 (optional)"
              placeholder="Apartment, floor (optional)"
              onChange={(e) => {
                setAddressLine2(e.target.value);
                clearFieldError("address_line2");
              }}
            />
            {fieldErrors.address_line1 ? (
              <p className="text-sm text-destructive">
                {fieldErrors.address_line1}
              </p>
            ) : null}
            {fieldErrors.address_line2 ? (
              <p className="text-sm text-destructive">
                {fieldErrors.address_line2}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label htmlFor="postal_code">Postal code *</Label>
              <Input
                id="postal_code"
                value={postalCode}
                autoComplete="postal-code"
                maxLength={POSTAL_CODE_MAX}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                  clearFieldError("postal_code");
                }}
                aria-invalid={!!fieldErrors.postal_code}
              />
              {fieldErrors.postal_code ? (
                <p className="text-sm text-destructive">
                  {fieldErrors.postal_code}
                </p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={city}
                autoComplete="address-level2"
                maxLength={CITY_MAX}
                onChange={(e) => {
                  setCity(e.target.value);
                  clearFieldError("city");
                }}
                aria-invalid={!!fieldErrors.city}
              />
              {fieldErrors.city ? (
                <p className="text-sm text-destructive">{fieldErrors.city}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="country_code">Country *</Label>
              <Input
                id="country_code"
                value={countryCode}
                autoComplete="country"
                maxLength={2}
                placeholder="DK"
                onChange={(e) => {
                  setCountryCode(normalizeCountry(e.target.value));
                  clearFieldError("country_code");
                }}
                aria-invalid={!!fieldErrors.country_code}
                aria-describedby="country_code_hint"
              />
              <p id="country_code_hint" className="text-xs opacity-60">
                2-letter code, e.g. DK, GB, DE.
              </p>
              {fieldErrors.country_code ? (
                <p className="text-sm text-destructive">
                  {fieldErrors.country_code}
                </p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium" id="business_status_label">
              I am applying as: *
            </p>
            <div
              role="radiogroup"
              aria-labelledby="business_status_label"
              aria-invalid={!!fieldErrors.business_status}
              className="flex flex-col gap-2 sm:flex-row sm:gap-6"
            >
              <label
                htmlFor="business_status_private"
                className="flex items-center gap-2 text-sm"
              >
                <input
                  id="business_status_private"
                  type="radio"
                  name="business_status"
                  value="private"
                  checked={businessStatus === "private"}
                  onChange={() => {
                    setBusinessStatus("private");
                    // A private applicant cannot carry a registration
                    // number, so drop any value typed before the switch
                    // rather than submitting one the server will refuse.
                    setRegistrationNumber("");
                    clearFieldError("business_status");
                    clearFieldError("business_registration_number");
                  }}
                />
                a private individual
              </label>
              <label
                htmlFor="business_status_business"
                className="flex items-center gap-2 text-sm"
              >
                <input
                  id="business_status_business"
                  type="radio"
                  name="business_status"
                  value="business"
                  checked={businessStatus === "business"}
                  onChange={() => {
                    setBusinessStatus("business");
                    clearFieldError("business_status");
                  }}
                />
                a business
              </label>
            </div>
            {fieldErrors.business_status ? (
              <p className="text-sm text-destructive">
                {fieldErrors.business_status}
              </p>
            ) : null}
          </div>

          {businessStatus === "business" ? (
            <div className="space-y-1">
              <Label htmlFor="business_registration_number">
                CVR or VAT registration number (only if you have one)
              </Label>
              <Input
                id="business_registration_number"
                value={registrationNumber}
                maxLength={BUSINESS_REG_NUMBER_MAX}
                onChange={(e) => {
                  setRegistrationNumber(e.target.value);
                  clearFieldError("business_registration_number");
                }}
                aria-invalid={!!fieldErrors.business_registration_number}
              />
              {fieldErrors.business_registration_number ? (
                <p className="text-sm text-destructive">
                  {fieldErrors.business_registration_number}
                </p>
              ) : null}
            </div>
          ) : null}
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="region">Region *</Label>
            <select
              id="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="language">Primary language *</Label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Key consent terms, stated in-form so accepting the checkbox is
            informed consent even before the full ToS doc is read. Keep in
            sync with the published creator-program ToS (Story 6.10). */}
        <div className="rounded-md border border-brand-forest-200 bg-brand-paper-50 p-4 text-sm space-y-2">
          <p className="font-semibold">Featuring &amp; consent — the short version</p>
          <ul className="list-disc pl-5 space-y-1 opacity-90">
            <li>
              By applying to be a content creator, you consent to being
              featured in portfolios and dossiers — both the portfolios you
              apply to and ones we assign you to directly. You can withdraw
              your consent to direct assignment at any time in your profile
              settings; you'll then only be featured in portfolios you
              applied to yourself.
            </li>
            <li>
              You can withdraw consent from any portfolio you're on. Doing
              so revokes your creator status.
            </li>
            <li>
              Withdrawal only applies to future dossier purchases:
              dossiers and certificates already sold keep featuring your
              profile as usual.
            </li>
          </ul>
        </div>

        <div className="flex items-start gap-2 text-sm">
          <input
            id="tos"
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => {
              setTosAccepted(e.target.checked);
              clearFieldError("tos");
            }}
            className="mt-1"
            aria-invalid={!!fieldErrors.tos}
          />
          <label htmlFor="tos" className="leading-relaxed">
            I have read and agree to the{" "}
            <a
              href="/legal/creator-program"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Greenhold creator-program terms
            </a>
            {activeTos ? ` (${activeTos.version_label})` : ""}.
          </label>
        </div>
        {fieldErrors.tos ? (
          <p className="text-sm text-destructive">{fieldErrors.tos}</p>
        ) : null}

        {/* One-primary-per-screen: Submit is primary; Save as draft is a
            secondary action beside it. */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            variant="primary"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? "Submitting…" : "Submit application"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveDraft}
            disabled={saveDraftMutation.isPending}
          >
            {saveDraftMutation.isPending ? "Saving…" : "Save as draft"}
          </Button>
          {draftSavedLabel && (
            <span
              className="text-xs opacity-60"
              data-testid="draft-saved-indicator"
            >
              Draft saved · {draftSavedLabel}
            </span>
          )}
        </div>
      </div>

      <CreatorApplicationCertPreview
        displayName={displayName}
        helloText={helloText}
        imagePreviewUrl={imagePreviewUrl}
        signaturePreviewUrl={signaturePreviewUrl}
      />
    </form>
  );
}
